import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  requestUserEmailChange,
  resetUserEmailChangeRequest,
  resetUserEmailChangeVerify,
  resetUserProfileUpdate,
  updateUserProfile,
  verifyUserEmailChange,
} from "../../actions/userActions";
import "./ProfileScreen.css";

function splitName(fullName = "") {
  const parts = String(fullName).trim().split(/\s+/).filter(Boolean);

  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" ") || "",
  };
}

function cleanText(value) {
  return String(value || "")
    .replace(/\s+/g, " ")
    .trim();
}

const TITLE_OPTIONS = ["", "Mr", "Mrs", "Ms", "Miss", "Dr", "Prof"];

export default function ProfileScreen() {
  const dispatch = useDispatch();

  const userAuth = useSelector((state) => state.userAuth || {});
  const profileUpdate = useSelector((state) => state.userUpdateProfile || {});
  const emailChangeRequest = useSelector(
    (state) => state.userEmailChangeRequest || {},
  );
  const emailChangeVerify = useSelector(
    (state) => state.userEmailChangeVerify || {},
  );

  const {
    userInfo = null,
    isAuthenticated = false,
    loading: authLoading = false,
    checked = true,
    error: authError,
  } = userAuth;

  const { loading: updateLoading, error: updateError, success } = profileUpdate;
  const {
    loading: emailRequestLoading,
    error: emailRequestError,
    success: emailRequestSuccess,
    message: emailRequestMessage = "",
    pendingEmail = "",
  } = emailChangeRequest;
  const {
    loading: emailVerifyLoading,
    error: emailVerifyError,
    success: emailVerifySuccess,
    message: emailVerifyMessage = "",
  } = emailChangeVerify;

  const parsedStoredName = splitName(userInfo?.name);

  const [title, setTitle] = useState(userInfo?.title || "");
  const [firstName, setFirstName] = useState(userInfo?.name || "");
  const [lastName, setLastName] = useState(
    userInfo?.lastName || parsedStoredName.lastName,
  );
  const [email, setEmail] = useState(userInfo?.email || "");
  const [newEmail, setNewEmail] = useState("");
  const [emailChangeCode, setEmailChangeCode] = useState("");
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    if (!userInfo) return;

    const parsed = splitName(userInfo.name);

    setTitle(userInfo.title || "");
    setFirstName(userInfo.name || parsed.firstName || "");
    setLastName(userInfo.lastName || parsed.lastName || "");
    setEmail(userInfo.email || "");
  }, [userInfo]);

  useEffect(() => {
    return () => {
      dispatch(resetUserProfileUpdate());
      dispatch(resetUserEmailChangeRequest());
      dispatch(resetUserEmailChangeVerify());
    };
  }, [dispatch]);

  const displayName = useMemo(() => {
    const fullName = cleanText(`${firstName} ${lastName}`);
    const titlePrefix = cleanText(title);

    return cleanText(`${titlePrefix} ${fullName}`) || "My Account";
  }, [title, firstName, lastName]);

  const submitHandler = (e) => {
    e.preventDefault();
    setLocalError("");

    const cleanedTitle = cleanText(title);
    const cleanedFirstName = cleanText(firstName);
    const cleanedLastName = cleanText(lastName);

    if (!TITLE_OPTIONS.includes(cleanedTitle)) {
      setLocalError("Please select a valid title");
      return;
    }

    if (cleanedFirstName.length < 2) {
      setLocalError("First name must be at least 2 characters");
      return;
    }

    if (cleanedFirstName.length > 100) {
      setLocalError("First name must be at most 100 characters");
      return;
    }

    if (!cleanedLastName) {
      setLocalError("Last name is required");
      return;
    }

    if (cleanedLastName.length > 100) {
      setLocalError("Last name must be at most 100 characters");
      return;
    }

    dispatch(
      updateUserProfile({
        title: cleanedTitle,
        name: cleanedFirstName,
        lastName: cleanedLastName,
      }),
    );
  };

  const emailChangeHandler = (e) => {
    e.preventDefault();
    setLocalError("");

    const trimmedEmail = newEmail.trim();

    if (!trimmedEmail) {
      setLocalError("Enter your new email address");
      return;
    }

    dispatch(requestUserEmailChange(trimmedEmail));
  };

  const emailVerifyHandler = (e) => {
    e.preventDefault();
    setLocalError("");

    const code = emailChangeCode.trim();

    if (!code) {
      setLocalError("Enter the verification code from your new email");
      return;
    }

    dispatch(verifyUserEmailChange(code));
  };

  if (!checked || authLoading) {
    return (
      <section className="profile-shell">
        <div className="profile-card">
          <p>Loading profile...</p>
        </div>
      </section>
    );
  }

  if (!isAuthenticated || !userInfo) {
    return (
      <section className="profile-shell">
        <div className="profile-card">
          <div className="profile-alert">
            Please sign in to view your profile.
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="profile-shell">
      <div className="profile-card">
        <div className="profile-head">
          <div>
            <h1 className="profile-title">My Profile</h1>
            <p className="profile-subtitle">
              Update your account details and keep your information current.
            </p>
          </div>

          <span className="profile-role">{displayName}</span>
        </div>

        {(localError ||
          updateError ||
          authError ||
          emailRequestError ||
          emailVerifyError) && (
          <div className="profile-alert">
            {localError ||
              updateError ||
              authError ||
              emailRequestError ||
              emailVerifyError}
          </div>
        )}

        {(success || emailRequestSuccess || emailVerifySuccess) && (
          <div className="profile-success">
            {emailVerifyMessage ||
              emailRequestMessage ||
              "Profile updated successfully."}
          </div>
        )}

        <form className="profile-form" onSubmit={submitHandler} noValidate>
          <div className="profile-field">
            <label htmlFor="profile-title-field">Title</label>
            <select
              id="profile-title-field"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoComplete="honorific-prefix"
            >
              <option value="">Prefer not to say</option>
              <option value="Mr">Mr</option>
              <option value="Mrs">Mrs</option>
              <option value="Ms">Ms</option>
              <option value="Miss">Miss</option>
              <option value="Dr">Dr</option>
              <option value="Prof">Prof</option>
            </select>
          </div>

          <div className="profile-field">
            <label htmlFor="profile-first-name">First name*</label>
            <input
              id="profile-first-name"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoComplete="given-name"
              minLength={2}
              maxLength={100}
              required
            />
          </div>

          <div className="profile-field">
            <label htmlFor="profile-last-name">Last name*</label>
            <input
              id="profile-last-name"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              autoComplete="family-name"
              maxLength={100}
              required
            />
          </div>

          <div className="profile-field">
            <label htmlFor="profile-email">Email address</label>
            <input
              id="profile-email"
              type="email"
              value={email}
              autoComplete="email"
              disabled
              readOnly
            />
          </div>

          <button
            type="submit"
            className="profile-btn"
            disabled={updateLoading}
          >
            {updateLoading ? "Saving..." : "Save changes"}
          </button>
        </form>

        <div className="profile-divider" />

        <form className="profile-form" onSubmit={emailChangeHandler} noValidate>
          <div className="profile-field">
            <label htmlFor="profile-new-email">New email address</label>
            <input
              id="profile-new-email"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              autoComplete="email"
            />
          </div>

          <button
            type="submit"
            className="profile-btn"
            disabled={emailRequestLoading}
          >
            {emailRequestLoading ? "Sending..." : "Send verification code"}
          </button>
        </form>

        {(pendingEmail || emailRequestSuccess) && (
          <form
            className="profile-form profile-form--stacked"
            onSubmit={emailVerifyHandler}
            noValidate
          >
            <div className="profile-field">
              <label htmlFor="profile-email-code">Verification code</label>
              <input
                id="profile-email-code"
                type="text"
                value={emailChangeCode}
                onChange={(e) => setEmailChangeCode(e.target.value)}
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                autoComplete="one-time-code"
              />
            </div>

            <button
              type="submit"
              className="profile-btn"
              disabled={emailVerifyLoading}
            >
              {emailVerifyLoading ? "Verifying..." : "Verify new email"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
